import { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import ViewFacultyTimetable from "../Admin/ViewFacultyTimetable.jsx";
import { FiCalendar, FiUser, FiFilter, FiSearch, FiBookOpen } from "react-icons/fi";

const Timetable = () => {
  const [activeTab, setActiveTab] = useState("class"); // "class" | "faculty"
  const [student, setStudent] = useState(null);
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingTimetable, setFetchingTimetable] = useState(false);

  // Filters for Class Timetable
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [sectionsList, setSectionsList] = useState(["A", "B", "C", "D"]);

  // Faculty Timetable States
  const [faculties, setFaculties] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState("");
  const [facultySearch, setFacultySearch] = useState("");

  const router = useLocation();
  const dispatch = useDispatch();

  // Fetch initial student details and branches
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Branches
        const branchRes = await axios.get(`${baseApiURL()}/branch/getBranch`);
        if (branchRes.data.success) {
          setBranches(branchRes.data.branches);
        }

        // Fetch Faculty List
        const facultyRes = await axios.get(`${baseApiURL()}/faculty/details/getDetails2`);
        if (facultyRes.data.success) {
          setFaculties(facultyRes.data.faculties || []);
        }

        // Fetch Student Profile Details
        if (router.state?.loginid && router.state?.type) {
          const studentRes = await axios.post(
            `${baseApiURL()}/${router.state.type}/details/getDetails`,
            { enrollmentNo: router.state.loginid },
            { headers: { "Content-Type": "application/json" } }
          );

          if (studentRes.data.success && studentRes.data.user[0]) {
            const userData = studentRes.data.user[0];
            setStudent(userData);
            setSelectedBranch(userData.branch || "");
            setSelectedSemester(userData.semester ? String(userData.semester) : "");
            setSelectedSection(userData.section || "A");
          }
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
        toast.error("Error fetching profile details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, router.state?.loginid, router.state?.type]);

  // Fetch dynamic sections list whenever branch or semester changes
  useEffect(() => {
    const fetchSections = async () => {
      try {
        if (!selectedBranch || !selectedSemester) return;
        const res = await axios.get(`${baseApiURL()}/section/getSection`, {
          params: { branch: selectedBranch, semester: selectedSemester }
        });
        if (res.data.success && res.data.sections && res.data.sections.length > 0) {
          const secNames = res.data.sections.map((s) => s.name);
          setSectionsList(secNames);
          if (!secNames.includes(selectedSection)) {
            setSelectedSection(secNames[0]);
          }
        } else {
          setSectionsList(["A", "B", "C", "D"]);
        }
      } catch (error) {
        console.error("Error fetching sections:", error);
        setSectionsList(["A", "B", "C", "D"]);
      }
    };

    fetchSections();
  }, [selectedBranch, selectedSemester]);

  // Auto-fetch class timetable whenever filters change
  useEffect(() => {
    const fetchClassTimetable = async () => {
      if (!selectedBranch || !selectedSemester) return;

      try {
        setFetchingTimetable(true);
        const res = await axios.get(`${baseApiURL()}/timetable/getTimetable`, {
          params: {
            branch: selectedBranch,
            semester: selectedSemester,
            section: selectedSection || "A"
          }
        });

        if (res.data.success && res.data.timetable) {
          setTimetable(res.data.timetable);
        } else {
          setTimetable(null);
        }
      } catch (error) {
        console.error("Error fetching class timetable:", error);
        setTimetable(null);
      } finally {
        setFetchingTimetable(false);
      }
    };

    if (activeTab === "class") {
      fetchClassTimetable();
    }
  }, [selectedBranch, selectedSemester, selectedSection, activeTab]);

  // Helper to calculate maximum periods across all days
  const maxPeriods = () => {
    if (!timetable || !timetable.schedule) return 0;
    return Math.max(...timetable.schedule.map((d) => (d.periods ? d.periods.length : 0)), 0);
  };

  // Filtered Faculty list for search dropdown
  const filteredFaculties = faculties.filter((f) => {
    const fullName = `${f.firstName || ''} ${f.lastName || ''} ${f.employeeId || ''}`.toLowerCase();
    return fullName.includes(facultySearch.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen p-8 bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-indigo-600 dark:text-indigo-400">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
          <span className="font-semibold text-slate-700 dark:text-slate-300">Loading Timetable Dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header Banner & Tab Switcher */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xs dark:shadow-xl border border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <FiCalendar className="text-indigo-600 dark:text-indigo-400" />
            Timetable Dashboard
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            View your class weekly schedule or search & view any faculty member's timetable.
          </p>
        </div>

        {/* Tab Toggle Buttons */}
        <div className="flex bg-slate-100 dark:bg-slate-950 p-1.5 rounded-xl border border-slate-200 dark:border-slate-800">
          <button
            onClick={() => setActiveTab("class")}
            className={`px-5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "class"
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800"
            }`}
          >
            <FiBookOpen className="w-4 h-4" />
            <span>Class Timetable</span>
          </button>

          <button
            onClick={() => setActiveTab("faculty")}
            className={`px-5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "faculty"
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800"
            }`}
          >
            <FiUser className="w-4 h-4" />
            <span>Faculty Timetable</span>
          </button>
        </div>
      </div>

      {/* CLASS TIMETABLE TAB */}
      {activeTab === "class" && (
        <div className="space-y-6">
          {/* Class Filters Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-xs border border-slate-200 dark:border-slate-800">
            <div className="flex items-center space-x-2 mb-4">
              <FiFilter className="text-indigo-600 dark:text-indigo-400 text-lg" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Class Timetable Filters</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">Branch</label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium outline-none"
                >
                  <option value="">Select Branch</option>
                  {branches.map((b) => (
                    <option key={b.name} value={b.name}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">Semester</label>
                <select
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium outline-none"
                >
                  <option value="">Select Semester</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                    <option key={sem} value={sem}>
                      Semester {sem}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">Section</label>
                <select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium outline-none"
                >
                  <option value="">Select Section</option>
                  {sectionsList.map((sec) => (
                    <option key={sec} value={sec}>
                      Section {sec}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Student Info Pill */}
          {student && (
            <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-wrap items-center justify-center gap-4 text-xs font-semibold">
              <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 rounded-lg border border-indigo-100 dark:border-indigo-500/20">
                Enrolled Branch: <strong className="text-indigo-900 dark:text-indigo-200">{student.branch}</strong>
              </span>
              <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 rounded-lg border border-indigo-100 dark:border-indigo-500/20">
                Semester: <strong className="text-indigo-900 dark:text-indigo-200">Sem {student.semester}</strong>
              </span>
              <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 rounded-lg border border-indigo-100 dark:border-indigo-500/20">
                Section: <strong className="text-indigo-900 dark:text-indigo-200">{student.section || 'A'}</strong>
              </span>
            </div>
          )}

          {/* Timetable Table */}
          {fetchingTimetable ? (
            <div className="bg-white dark:bg-slate-900 p-12 rounded-2xl border border-slate-200 dark:border-slate-800 text-center text-slate-500 dark:text-slate-400">
              <p className="text-sm font-medium">Fetching timetable...</p>
            </div>
          ) : timetable ? (
            <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs bg-white dark:bg-slate-900">
              <table className="min-w-[1200px] w-full text-left">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    <th className="py-3.5 px-4">Day</th>
                    {Array.from({ length: maxPeriods() }).map((_, i) => (
                      <th key={i} className="py-3.5 px-4">Period {i + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                  {timetable.schedule.map((dayEntry, index) => (
                    <tr key={dayEntry.day} className="hover:bg-slate-50 dark:hover:bg-slate-850 transition-colors">
                      <td className="font-bold text-xs text-indigo-600 dark:text-indigo-400 py-4 px-4">{dayEntry.day}</td>
                      {Array.from({ length: maxPeriods() }).map((_, i) => {
                        const period = dayEntry.periods ? dayEntry.periods[i] : null;
                        return (
                          <td key={i} className="py-4 px-4 text-xs text-slate-700 dark:text-slate-300">
                            {period ? (
                              <div className="space-y-1">
                                <span className="block font-bold text-slate-900 dark:text-white">{period.subject}</span>
                                <span className="block text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                  {[period.faculty?.firstName, period.faculty?.lastName].filter(Boolean).join(' ')}
                                </span>
                                <span className="inline-block text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-100 dark:border-indigo-500/20">
                                  {period.startTime} - {period.endTime}
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-400 dark:text-slate-600">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-800 shadow-xs">
              <FiCalendar className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-base font-bold text-slate-800 dark:text-slate-200">No Timetable Found</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                No timetable published for {selectedBranch} Semester {selectedSemester} Section {selectedSection || 'A'}.
              </p>
            </div>
          )}
        </div>
      )}

      {/* FACULTY TIMETABLE TAB */}
      {activeTab === "faculty" && (
        <div className="space-y-6">
          {/* Faculty Search & Selection Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-xs border border-slate-200 dark:border-slate-800">
            <div className="flex items-center space-x-2 mb-4">
              <FiUser className="text-indigo-600 dark:text-indigo-400 text-lg" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Select Faculty Member</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Search Faculty Name / ID
                </label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-3 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by name or employee ID..."
                    value={facultySearch}
                    onChange={(e) => setFacultySearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Choose Faculty ({filteredFaculties.length})
                </label>
                <select
                  value={selectedFaculty}
                  onChange={(e) => setSelectedFaculty(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium outline-none"
                >
                  <option value="">-- Select Faculty Member --</option>
                  {filteredFaculties.map((f) => (
                    <option key={f._id} value={f.employeeId}>
                      {f.firstName} {f.lastName} ({f.employeeId}) - {f.department || 'General'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Render Selected Faculty Timetable */}
          {selectedFaculty ? (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-xs border border-slate-200 dark:border-slate-800">
              <ViewFacultyTimetable facultyId={selectedFaculty} />
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-800 shadow-xs">
              <FiUser className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-base font-bold text-slate-800 dark:text-slate-200">Select a Faculty Member</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Choose a faculty member from the dropdown above to view their weekly timetable.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Timetable;